import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Admin Impersonation — 2-tier role sistemi
 *
 * - "anonymous"     → giriş yapmamış ziyaretçi (eski "guest")
 * - "guest_member"  → mailli ücretsiz üye (eski "starter")
 * - "premium"       → ücretli premium üye (eski "premium" + "elite" birleşti)
 *
 * Eski URL/storage değerleri (guest, starter, elite) otomatik map'lenir.
 */

export type ImpersonationRole = null | "anonymous" | "guest_member" | "premium";
export type ImpersonationStatus = "active" | "canceled" | "expired";

const STORAGE_KEY = "lovable.impersonation.role";
const STORAGE_KEY_STATUS = "lovable.impersonation.status";
const STORAGE_KEY_ENDS = "lovable.impersonation.ends";
const STORAGE_KEY_ARMED = "lovable.impersonation.armed";
const URL_PARAM = "as";
const URL_PARAM_STATUS = "status";
const URL_PARAM_ENDS = "ends";

const DEFAULT_ENDS_IN_DAYS = 14;

const ADMIN_PATH_PREFIX = "/admin";
const isOnAdminPath = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith(ADMIN_PATH_PREFIX);
const isArmed = () =>
  typeof window !== "undefined" && window.sessionStorage.getItem(STORAGE_KEY_ARMED) === "1";
const setArmed = (on: boolean) => {
  if (typeof window === "undefined") return;
  if (on) window.sessionStorage.setItem(STORAGE_KEY_ARMED, "1");
  else window.sessionStorage.removeItem(STORAGE_KEY_ARMED);
};

/** Eski rol adlarını yeni 2-tier sistemine çevir. */
const migrateRole = (v: string | null): Exclude<ImpersonationRole, null> | null => {
  if (!v) return null;
  if (v === "anonymous" || v === "guest_member" || v === "premium") return v;
  // backwards compat
  if (v === "guest") return "anonymous";
  if (v === "starter") return "guest_member";
  if (v === "elite") return "premium";
  return null;
};

const isStatus = (v: string | null): v is ImpersonationStatus =>
  v === "active" || v === "canceled" || v === "expired";

const parseEnds = (v: string | null): number | null => {
  if (v === null) return null;
  const n = Number.parseInt(v, 10);
  if (!Number.isFinite(n)) return null;
  return Math.max(-365, Math.min(365, n));
};

const getUrl = () => (typeof window === "undefined" ? null : new URLSearchParams(window.location.search));

const readRoleFromUrl = (): ImpersonationRole => migrateRole(getUrl()?.get(URL_PARAM) ?? null);
const readStatusFromUrl = (): ImpersonationStatus | null => {
  const v = getUrl()?.get(URL_PARAM_STATUS) ?? null;
  return isStatus(v) ? v : null;
};
const readEndsFromUrl = (): number | null => parseEnds(getUrl()?.get(URL_PARAM_ENDS) ?? null);

const readStoredRole = (): ImpersonationRole => {
  if (typeof window === "undefined") return null;
  const fromUrl = readRoleFromUrl();
  if (fromUrl && isOnAdminPath()) {
    setArmed(true);
    return fromUrl;
  }
  if (!isArmed()) return null;
  if (fromUrl) return fromUrl;
  return migrateRole(window.localStorage.getItem(STORAGE_KEY));
};
const readStoredStatus = (): ImpersonationStatus => {
  if (typeof window === "undefined") return "active";
  const fromUrl = readStatusFromUrl();
  if (fromUrl) return fromUrl;
  const v = window.localStorage.getItem(STORAGE_KEY_STATUS);
  return isStatus(v) ? v : "active";
};
const readStoredEnds = (): number => {
  if (typeof window === "undefined") return DEFAULT_ENDS_IN_DAYS;
  const fromUrl = readEndsFromUrl();
  if (fromUrl !== null) return fromUrl;
  const v = parseEnds(window.localStorage.getItem(STORAGE_KEY_ENDS));
  return v ?? DEFAULT_ENDS_IN_DAYS;
};

const writeUrl = (
  role: ImpersonationRole,
  status: ImpersonationStatus,
  endsInDays: number,
) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (role) {
    url.searchParams.set(URL_PARAM, role);
    if (role === "anonymous") {
      url.searchParams.delete(URL_PARAM_STATUS);
      url.searchParams.delete(URL_PARAM_ENDS);
    } else {
      url.searchParams.set(URL_PARAM_STATUS, status);
      url.searchParams.set(URL_PARAM_ENDS, String(endsInDays));
    }
  } else {
    url.searchParams.delete(URL_PARAM);
    url.searchParams.delete(URL_PARAM_STATUS);
    url.searchParams.delete(URL_PARAM_ENDS);
  }
  window.history.replaceState(window.history.state, "", url.toString());
};

interface ImpersonationContextType {
  realUser: User | null;
  realIsAdmin: boolean;
  loading: boolean;
  role: ImpersonationRole;
  status: ImpersonationStatus;
  endsInDays: number;
  setRole: (role: ImpersonationRole) => void;
  setStatus: (status: ImpersonationStatus) => void;
  setEndsInDays: (days: number) => void;
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  realUser: null,
  realIsAdmin: false,
  loading: true,
  role: null,
  status: "active",
  endsInDays: DEFAULT_ENDS_IN_DAYS,
  setRole: () => {},
  setStatus: () => {},
  setEndsInDays: () => {},
  isImpersonating: false,
});

export const useImpersonation = () => useContext(ImpersonationContext);

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [realIsAdmin, setRealIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [role, setRoleState] = useState<ImpersonationRole>(() => readStoredRole());
  const [status, setStatusState] = useState<ImpersonationStatus>(() => readStoredStatus());
  const [endsInDays, setEndsInDaysState] = useState<number>(() => readStoredEnds());

  useEffect(() => {
    let cancelled = false;
    const sync = async (uid: string | null) => {
      if (!uid) {
        if (!cancelled) {
          setRealIsAdmin(false);
          setLoading(false);
        }
        return;
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      if (cancelled) return;
      const isAdmin = !!data;
      setRealIsAdmin(isAdmin);
      if (!isAdmin) {
        setRoleState(null);
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(STORAGE_KEY);
          window.localStorage.removeItem(STORAGE_KEY_STATUS);
          window.localStorage.removeItem(STORAGE_KEY_ENDS);
        }
      }
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      const u = session?.user ?? null;
      setRealUser(u);
      setTimeout(() => sync(u?.id ?? null), 0);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setRealUser(u);
      sync(u?.id ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const setRole = useCallback((next: ImpersonationRole) => {
    setRoleState((current) => {
      if (next !== null && !realIsAdmin) return current;
      if (next !== null && !isOnAdminPath() && !isArmed()) {
        if (typeof window !== "undefined") {
          // eslint-disable-next-line no-console
          console.warn("[impersonation] activation blocked: must be initiated from /admin");
        }
        return current;
      }
      if (typeof window !== "undefined") {
        if (next === null) {
          window.localStorage.removeItem(STORAGE_KEY);
          setArmed(false);
        } else {
          window.localStorage.setItem(STORAGE_KEY, next);
          setArmed(true);
        }
      }
      writeUrl(next, status, endsInDays);
      return next;
    });
  }, [realIsAdmin, status, endsInDays]);

  const setStatus = useCallback((next: ImpersonationStatus) => {
    if (!realIsAdmin) return;
    setStatusState(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY_STATUS, next);
    writeUrl(role, next, endsInDays);
  }, [realIsAdmin, role, endsInDays]);

  const setEndsInDays = useCallback((days: number) => {
    if (!realIsAdmin) return;
    const clamped = Math.max(-365, Math.min(365, Math.trunc(days)));
    setEndsInDaysState(clamped);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY_ENDS, String(clamped));
    writeUrl(role, status, clamped);
  }, [realIsAdmin, role, status]);

  useEffect(() => {
    if (!realIsAdmin) return;
    if (
      readRoleFromUrl() !== role ||
      (role && role !== "anonymous" && (readStatusFromUrl() !== status || readEndsFromUrl() !== endsInDays))
    ) {
      writeUrl(role, status, endsInDays);
    }
  });

  useEffect(() => {
    const onPop = () => {
      if (!realIsAdmin) return;
      const urlRole = readRoleFromUrl();
      if (urlRole && isOnAdminPath()) setArmed(true);
      const armed = isArmed();
      const r = armed ? urlRole : null;
      const s = readStatusFromUrl() ?? "active";
      const e = readEndsFromUrl() ?? DEFAULT_ENDS_IN_DAYS;
      setRoleState(r);
      setStatusState(s);
      setEndsInDaysState(e);
      if (typeof window !== "undefined") {
        if (r) window.localStorage.setItem(STORAGE_KEY, r);
        else window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.setItem(STORAGE_KEY_STATUS, s);
        window.localStorage.setItem(STORAGE_KEY_ENDS, String(e));
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [realIsAdmin]);

  const value = useMemo<ImpersonationContextType>(() => ({
    realUser,
    realIsAdmin,
    loading,
    role: realIsAdmin ? role : null,
    status,
    endsInDays,
    setRole,
    setStatus,
    setEndsInDays,
    isImpersonating: realIsAdmin && role !== null,
  }), [realUser, realIsAdmin, loading, role, status, endsInDays, setRole, setStatus, setEndsInDays]);

  return (
    <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>
  );
};

/** Map an impersonation role to the synthesized plan name used by `hasAccess`. */
export const impersonationPlanName = (role: ImpersonationRole): string | null => {
  if (role === "guest_member") return "GUEST_MEMBER";
  if (role === "premium") return "PREMIUM";
  return null;
};
